import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { CreditCard, Edit2, Plus, Trash2 } from 'lucide-react'
import { satPlansApi, type SatPlan } from '../../api/satPlans'

const PLATFORM_TYPE_OPTIONS = [
  { value: 'tutor', label: 'Tutoring' },
  { value: 'self_pace', label: 'Self-Paced' },
]

interface SatPlanFormState {
  plan_code: string
  name: string
  priceDollars: string | number
  description: string
  is_active: boolean
  platform_type: string
}

const emptyForm: SatPlanFormState = {
  plan_code: '',
  name: '',
  priceDollars: '',
  description: '',
  is_active: true,
  platform_type: 'tutor',
}

function formatPrice(priceInCent: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(priceInCent / 100)
}

export function SatPlanMgr() {
  const [plans, setPlans] = useState<SatPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<SatPlan | null>(null)
  const [form, setForm] = useState<SatPlanFormState>(emptyForm)

  const activeCount = useMemo(() => plans.filter((plan) => plan.is_active).length, [plans])

  useEffect(() => {
    fetchPlans()
  }, [])

  async function fetchPlans() {
    setLoading(true)
    try {
      const res = await satPlansApi.list()
      setPlans(res.data)
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load SAT plans',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }

  function openCreateModal() {
    setEditingPlan(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  function openEditModal(plan: SatPlan) {
    setEditingPlan(plan)
    setForm({
      plan_code: plan.plan_code,
      name: plan.name,
      priceDollars: plan.price_in_cent / 100,
      description: plan.description ?? '',
      is_active: plan.is_active,
      platform_type: plan.platform_type ?? 'tutor',
    })
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingPlan(null)
    setForm(emptyForm)
  }

  async function handleSubmit() {
    const planCode = form.plan_code.trim()
    const name = form.name.trim()
    const parsedPrice = typeof form.priceDollars === 'number'
      ? form.priceDollars
      : Number(form.priceDollars)

    if (!planCode || !name || form.priceDollars === '' || Number.isNaN(parsedPrice)) {
      notifications.show({
        title: 'Missing fields',
        message: 'Plan code, name, and price are required.',
        color: 'red',
      })
      return
    }

    setSaving(true)
    try {
      const payload = {
        plan_code: planCode,
        name,
        price_in_cent: Math.round(parsedPrice * 100),
        description: form.description.trim() || null,
        is_active: form.is_active,
        platform_type: form.platform_type,
      }

      if (editingPlan) {
        await satPlansApi.update(editingPlan.id, payload)
        notifications.show({
          title: 'Updated',
          message: 'SAT plan updated successfully.',
          color: 'green',
        })
      } else {
        await satPlansApi.create(payload)
        notifications.show({
          title: 'Created',
          message: 'SAT plan created successfully.',
          color: 'green',
        })
      }

      closeModal()
      fetchPlans()
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to save SAT plan',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(plan: SatPlan) {
    if (!window.confirm(`Delete SAT plan "${plan.name}"? This cannot be undone.`)) {
      return
    }

    try {
      await satPlansApi.delete(plan.id)
      notifications.show({
        title: 'Deleted',
        message: 'SAT plan deleted.',
        color: 'orange',
      })
      fetchPlans()
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to delete SAT plan',
        color: 'red',
      })
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Box>
            <Group gap="sm" align="center">
              <Title order={2} c="var(--sat-text)">SAT Plan Management</Title>
              <Badge color="gray" variant="light" size="lg">{plans.length} total</Badge>
              <Badge color="green" variant="light" size="lg">{activeCount} active</Badge>
            </Group>
            <Text size="sm" c="var(--sat-text-muted)" mt={6}>
              Manage subscription plan codes, pricing, and availability for the platform.
            </Text>
          </Box>

          <Button
            leftSection={<Plus size={16} />}
            color="grapefruit"
            radius="xl"
            onClick={openCreateModal}
          >
            Create SAT Plan
          </Button>
        </Group>

        {plans.length === 0 ? (
          <Paper radius="xl" p="xl" withBorder style={{ textAlign: 'center', background: 'var(--sat-surface)' }}>
            <CreditCard size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
            <Text fw={600} c="var(--sat-text)" mb={4}>No SAT plans yet</Text>
            <Text size="sm" c="var(--sat-text-muted)" mb="lg">
              Create the first subscription plan to start managing pricing options.
            </Text>
            <Button radius="xl" color="grapefruit" onClick={openCreateModal}>
              Add First Plan
            </Button>
          </Paper>
        ) : (
          <Paper radius="xl" p="md" withBorder style={{ background: 'var(--sat-surface)' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th>Platform</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ width: 110 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {plans.map((plan) => (
                  <Table.Tr key={plan.id}>
                    <Table.Td>
                      <Badge color="blue" variant="light">{plan.plan_code}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={600} size="sm" c="var(--sat-text)">{plan.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{formatPrice(plan.price_in_cent)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={plan.platform_type === 'self_pace' ? 'violet' : 'blue'} variant="light">
                        {plan.platform_type === 'self_pace' ? 'Self-Paced' : 'Tutoring'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={plan.is_active ? 'green' : 'gray'} variant="light">
                        {plan.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text-muted)" lineClamp={2}>
                        {plan.description || 'No description'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <ActionIcon variant="subtle" color="blue" onClick={() => openEditModal(plan)}>
                          <Edit2 size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(plan)}>
                          <Trash2 size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={editingPlan ? 'Edit SAT Plan' : 'Create SAT Plan'}
        size="md"
        radius="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Plan Code"
            placeholder="e.g. trial, monthly, premium"
            value={form.plan_code}
            onChange={(event) => {
              const value = event.currentTarget.value
              setForm((prev) => ({ ...prev, plan_code: value }))
            }}
            required
          />

          <TextInput
            label="Plan Name"
            placeholder="e.g. Monthly"
            value={form.name}
            onChange={(event) => {
              const value = event.currentTarget.value
              setForm((prev) => ({ ...prev, name: value }))
            }}
            required
          />

          <NumberInput
            label="Price (USD)"
            placeholder="0.00"
            value={form.priceDollars}
            onChange={(value) => setForm((prev) => ({ ...prev, priceDollars: value }))}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            required
          />

          <Textarea
            label="Description"
            placeholder="Short description shown to admins"
            value={form.description}
            onChange={(event) => {
              const value = event.currentTarget.value
              setForm((prev) => ({ ...prev, description: value }))
            }}
            rows={3}
          />

          <Select
            label="Platform Type"
            data={PLATFORM_TYPE_OPTIONS}
            value={form.platform_type}
            onChange={(value) => setForm((prev) => ({ ...prev, platform_type: value ?? 'tutor' }))}
            allowDeselect={false}
          />

          <Switch
            label="Active plan"
            checked={form.is_active}
            onChange={(event) => {
              const checked = event.currentTarget.checked
              setForm((prev) => ({ ...prev, is_active: checked }))
            }}
            color="green"
          />

          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="grapefruit" radius="xl" loading={saving} onClick={handleSubmit}>
              {editingPlan ? 'Save Changes' : 'Create Plan'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
